import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/Layout';
import Measurement from './components/Measurement';
import ConsentBanner from './components/ConsentBanner';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';

// Rota bazlı code-splitting: her sayfa kendi chunk'unda yüklenir.
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const GuideCategory = lazy(() => import('./pages/GuideCategory'));
const Services = lazy(() => import('./pages/Services'));
const NotFound = lazy(() => import('./pages/NotFound'));
const TeklifFormu = lazy(() => import('./pages/TeklifFormu'));
const LocalSeoLanding = lazy(() => import('./pages/LocalSeoLanding'));
const Fiyatlar = lazy(() => import('./pages/Fiyatlar'));
const Privacy = lazy(() => import('./pages/Privacy'));

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView();
      });
      return () => window.cancelAnimationFrame(frame);
    }

    window.scrollTo(0, 0);
    return undefined;
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function PageLoading() {
  return (
    <div className="flex min-h-[calc(100vh-5rem)] items-center justify-center" role="status" aria-label="Yükleniyor">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
    </div>
  );
}

function App() {
  const vercelInsightsEnabled = process.env.REACT_APP_VERCEL_INSIGHTS_ENABLED !== 'false'
    && !['localhost', '127.0.0.1'].includes(window.location.hostname);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Measurement />
        <Layout>
          {vercelInsightsEnabled && <SpeedInsights />}
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/hakkimizda" element={<About />} />
              <Route path="/iletisim" element={<Contact />} />
              {/* İlçe alt sayfaları canlıda 301 ile /:district'a yönlendirilir;
                  bu rotalar yalnızca yerel geliştirme içindir. */}
              <Route path="/kepenk-servisi/:district" element={<LocalSeoLanding kind="hub" />} />
              <Route path="/kepenk-tamiri/:district" element={<LocalSeoLanding kind="hub" />} />
              <Route path="/kepenk-servisi" element={<ServiceDetail service="kepenk-servisi" />} />
              <Route path="/kepenk-tamiri" element={<ServiceDetail service="kepenk-tamiri" />} />
              <Route path="/sifir-kepenk" element={<ServiceDetail service="sifir-kepenk" />} />
              <Route path="/kepenk-parcalari" element={<ServiceDetail service="kepenk-parcalari" />} />
              <Route path="/kepenk-fiyatlari" element={<Fiyatlar />} />
              <Route path="/:district" element={<LocalSeoLanding kind="hub" />} />

              {/* Bilgi Merkezi Rotaları */}
              <Route path="/bilgi-merkezi" element={<KnowledgeBase />} />
              <Route path="/bilgi-merkezi/rehber/:slug" element={<GuideDetail />} />
              <Route path="/bilgi-merkezi/kategori/:categoryId" element={<GuideCategory />} />
              <Route path="/hizmetlerimiz" element={<Services />} />
              <Route path="/teklif-formu" element={<TeklifFormu />} />
              <Route path="/gizlilik" element={<Privacy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
        <ConsentBanner />
        {vercelInsightsEnabled && <Analytics />}
      </Router>
    </HelmetProvider>
  );
}

export default App;
